import ArrowForwardIcon from "@mui/icons-material/ArrowForwardRounded";
import { Autocomplete, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type { ChangeCompanyEventHubsRequestTarget } from "../../../api/clientsManagementTypes";
import { companyEventHubsCaptions } from "../../../api/clientsManagementTypes";
import { api } from "../../../api/operationsApi";
import { useExecutionState } from "../../../hooks/useExecutionState";
import ExecutionStatusAlert from "../../common/ExecutionStatusAlert";
import FieldHelp from "../../common/FieldHelp";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import PageTitle from "../../common/PageTitle";

const changeClientEventHubs = withErrorHandling(api.dwhSync.changeClientEventHubs);

const DwhSyncContent = () => {
  const { executionState, setExecutionState } = useExecutionState();
  const [selectedClientCode, setSelectedClientCode] = useState<string | null>(null);

  const [clientsResp, fetchError, { fetch, isFetching }] = useFetch(api.dwhSync.getClientsWithEventHubs);

  const allClientCodes = useMemo(
    () =>
      Object.values(clientsResp?.clientsByTarget ?? {})
        .flatMap((clientCodes) => clientCodes)
        .sort(),
    [clientsResp?.clientsByTarget]
  );

  const allTargets = useMemo(
    () => Object.keys(clientsResp?.clientsByTarget ?? {}) as ChangeCompanyEventHubsRequestTarget[],
    [clientsResp?.clientsByTarget]
  );

  const fromTarget = useMemo(
    () =>
      clientsResp?.clientsByTarget && selectedClientCode
        ? allTargets.find((target) => defined(clientsResp.clientsByTarget[target]).includes(selectedClientCode))
        : undefined,
    [allTargets, clientsResp, selectedClientCode]
  );

  const toTarget = useMemo(
    () => (fromTarget ? allTargets.find((target) => fromTarget !== target) : undefined),
    [allTargets, fromTarget]
  );

  if (fetchError) {
    return <LoadingFailedAlert apiError={fetchError} />;
  }

  if (clientsResp === undefined) {
    return <InlineLoader />;
  }

  const handleExecute = async () => {
    if (!selectedClientCode || !toTarget) {
      return;
    }

    setExecutionState({ status: "in-progress" });

    const [resp, error] = await changeClientEventHubs({
      clientCode: selectedClientCode,
      target: toTarget,
    });

    if (error) {
      setExecutionState({ status: "error", error });
      return;
    }

    if (!resp.success) {
      setExecutionState({ status: "error", errorMessage: "Operation did not succeed." });
      return;
    }

    setExecutionState({
      status: "success",
      details: ["Post-change instructions:", `${resp.postChangeInstructions || "none"}`],
    });

    fetch();
  };

  return (
    <Stack spacing={2} sx={{ alignItems: "flex-start", width: "24rem" }}>
      <PageTitle title="Change Client Event Hubs" subtitle="🔄 About this operation" defaultExpanded>
        Switch a client between Event Hub configurations for Data Warehouse synchronization
        <br />
        <br />
        <strong>Event Hub Types:</strong>
        <br />• <strong>Per-organization:</strong> Shared Event Hubs across all companies in the organization
        (cost-effective)
        <br />• <strong>Dedicated per-company:</strong> Isolated Event Hubs for a specific company (better isolation and
        performance)
        <br />
        <br />
        ⚙️ <strong>Process:</strong> Changes Event Hub configuration in Azure, updates DWH application properties, and
        reconfigures Business Central connections
        <br />
        <br />
        ⚠️ <strong>Note:</strong> After switching to per-organization, old Event Hubs must be manually deleted and DWH
        Manager service restarted
      </PageTitle>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <Autocomplete
          fullWidth
          options={allClientCodes}
          value={selectedClientCode}
          onChange={(_, value) => setSelectedClientCode(value)}
          renderInput={(params) => <TextField {...params} label="Client code" />}
        />
        <FieldHelp>
          Select the client to switch between Event Hub configurations.
          <br />
          <br />
          <strong>What happens:</strong>
          <br />
          • Creates/configures Event Hubs in Azure
          <br />
          • Updates DWH application properties
          <br />
          • Reconfigures Business Central connections
          <br />
          • Updates Event Hub subscriptions
          <br />
          <br />
          <strong>Switching to Per-organization:</strong> Old dedicated Event Hubs remain and must be manually deleted,
          then DWH Manager service must be restarted.
          <br />
          <br />
          <strong>Validation:</strong> After switching, make a change in Business Central and verify it appears in
          Snowflake.
        </FieldHelp>
      </Stack>
      {selectedClientCode && fromTarget && toTarget && !isFetching && (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="subtitle2">{companyEventHubsCaptions[fromTarget]}</Typography>
          <ArrowForwardIcon color="action" />
          <Typography variant="subtitle2">{companyEventHubsCaptions[toTarget]}</Typography>
        </Stack>
      )}
      {selectedClientCode && isFetching && <CircularProgress size={24} />}
      {selectedClientCode && (
        <Button
          variant="contained"
          onClick={handleExecute}
          loading={executionState.status === "in-progress"}
          disabled={isFetching}
        >
          Change
        </Button>
      )}
      <ExecutionStatusAlert executionState={executionState} setExecutionState={setExecutionState} />
    </Stack>
  );
};

export default DwhSyncContent;
